<section class="px-6 py-20 sm:px-10">
  <article class="mx-auto max-w-3xl">
    <header class="mb-10">
      <span class="mb-4 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
        >Legal</span
      >
      <h1 class="font-display text-3xl font-semibold text-parchment sm:text-4xl">
        Ghoul Pool Privacy Policy
      </h1>
      <p class="mt-3 text-sm text-dusk">Last updated: {{ lastUpdated }}</p>
    </header>

    <section id="who-we-are" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">1. Who we are</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Ghoul Pool is operated by {{ legalEntity }} of Saint Charles, Illinois. Questions,
        corrections, and deletion requests:
        <a href="mailto:{{ contactEmail }}" class="text-parchment hover:underline">{{
          contactEmail
        }}</a
        >.
      </p>
    </section>

    <section id="what-we-collect" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">2. What we collect</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        <em class="not-italic font-medium text-parchment">Account information</em> — your email
        address, your display name, and your password, which is stored only as a bcrypt hash. We
        never see or store the password itself.
      </p>
      <p class="mt-3 leading-relaxed text-dusk">
        <em class="not-italic font-medium text-parchment">Game data</em> — the competitions you
        create or join, your picks, achievements you're awarded, invite codes, and the timestamps
        attached to all of it.
      </p>
      <p class="mt-3 leading-relaxed text-dusk">
        <em class="not-italic font-medium text-parchment">Technical information</em> — your IP
        address is read on sign-in and registration requests so we can rate-limit them; it is not
        written to our database. When the server hits an unexpected error it sends the error
        message, the URL, and sometimes your user ID to a monitoring service we run ourselves. Our
        web host keeps standard server logs.
      </p>
      <p class="mt-3 leading-relaxed text-dusk">
        <em class="not-italic font-medium text-parchment">Email records</em> — outbound mail we send
        you is queued in our database, including the address, subject, and body, so we can retry a
        failed delivery and see what was sent.
      </p>
      <p class="mt-3 leading-relaxed text-dusk">
        <em class="not-italic font-medium text-parchment">Site usage</em> — we run a self-hosted
        Umami analytics service to see which pages get visited and how many people use the site.
        It uses no cookies, doesn't store your IP address, and doesn't follow you to other sites.
        It records the page you viewed, along with details such as your browser, device type, and
        general location, and it is not linked to your account.
      </p>
    </section>

    <section id="what-we-dont-collect" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">3. What we don't collect</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        No payment information — there's nothing to pay for yet. No advertising trackers, no
        third-party scripts, no embedded social widgets. Our fonts are served from our own server
        rather than a font CDN, so loading a page doesn't tell anyone else you were here. We do not
        sell your data, and we won't.
      </p>
    </section>

    <section id="cookies" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">4. Cookies</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Ghoul Pool sets one cookie: an httpOnly session cookie holding your refresh token, which is
        what keeps you signed in between visits. It expires after 30 days and is deleted when you
        log out. Your short-lived access token is held in the browser's memory and never written to
        storage. There are no advertising or analytics cookies, which is why you aren't reading this
        in a pop-up.
      </p>
    </section>

    <section id="email-we-send-you" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">5. Email we send you</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Account mail — verifying your address and resetting your password — is part of having an
        account and can't be turned off while you have one. Game mail tells you when one of your
        picks has died or when something changes in a competition you're in. Leaving a competition
        stops its mail; closing your account stops all of it.
      </p>
    </section>

    <section id="who-else-touches-your-data" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">
        6. Who else touches your data
      </h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Our email provider, to deliver the mail above. Our web host. The monitoring service
        described in section 2, which we operate ourselves on our own hardware. We also call
        Wikipedia and Wikidata to look up celebrity information — those requests come from our
        server and carry nothing about you.
      </p>
    </section>

    <section id="information-about-other-people" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">
        7. Information about other people
      </h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Ghoul Pool keeps a catalog of public figures — names, dates, causes of death, and
        photographs — drawn from public sources. If you are one of those people, or represent one,
        and you want an entry corrected or removed, write to
        <a href="mailto:{{ contactEmail }}" class="text-parchment hover:underline">{{
          contactEmail
        }}</a>
        and we'll deal with it.
      </p>
    </section>

    <section id="how-long-we-keep-things" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">8. How long we keep things</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Account and competition data stays until you ask us to delete it, so that past pools remain
        viewable by the people who played in them. Refresh tokens expire after 30 days and are
        revoked when you log out or change your password. Queued email records are kept as a
        delivery history. When you close your account we remove your personal details; competitions
        you ran may survive in a form that no longer identifies you, so that other players don't
        lose their history.
      </p>
    </section>

    <section id="your-choices" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">9. Your choices</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Write to
        <a href="mailto:{{ contactEmail }}" class="text-parchment hover:underline">{{
          contactEmail
        }}</a>
        to see what we hold about you, correct it, or have it deleted. We'll respond within 30 days.
        If you live somewhere with stronger data rights than Illinois, tell us and we'll honor a
        reasonable request rather than argue about jurisdiction.
      </p>
    </section>

    <section id="security" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">10. Security</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Passwords are hashed with bcrypt. Refresh tokens are stored hashed and rotate on every use.
        Sign-in and registration are rate-limited. The site runs over HTTPS. None of this makes any
        system perfectly secure, and we won't pretend otherwise — if we ever have a breach that
        affects you, we'll tell you.
      </p>
    </section>

    <section id="children" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">11. Children</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        Ghoul Pool is for adults. We don't knowingly collect anything from anyone under 18, and
        we'll delete an account we find out belongs to a minor.
      </p>
    </section>

    <section id="changes-to-this-policy" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">12. Changes to this policy</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        We'll post the new version here and change the date. If a change materially affects you,
        we'll email you.
      </p>
    </section>

    <section id="contact" class="border-t border-hairline py-7">
      <h2 class="font-display text-lg font-semibold text-parchment">13. Contact</h2>
      <p class="mt-3 leading-relaxed text-dusk">
        <a href="mailto:{{ contactEmail }}" class="text-parchment hover:underline">{{
          contactEmail
        }}</a
        >.
      </p>
    </section>

    <p class="mt-8 text-sm text-dusk">
      See also our
      <a routerLink="/terms" class="text-parchment hover:underline">Terms of Service</a>.
    </p>
  </article>
</section>
